import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import { useOrganizationStore } from '../../store/organization.store';
import { useNotificationsStore } from '../../store/notifications.store';
import { getAuth } from 'firebase/auth';
import {
  subscribePendingLicenses,
  subscribeRejectedLicenseRequests,
  claimPendingLicense,
  dismissRejectedLicenseRequest,
  getNotificationPreferences,
  PendingLicense,
  RejectedLicenseRequest as RejectedRequest,
} from '../../services/notification.service';

interface PendingLicensesModalProps {
  firebase: any;
}

const PendingLicensesModal: React.FC<PendingLicensesModalProps> = ({ firebase }) => {
  const [pendingLicenses, setPendingLicenses] = useState<PendingLicense[]>([]);
  const [rejectedRequests, setRejectedRequests] = useState<RejectedRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [claimingIds, setClaimingIds] = useState<Set<string>>(new Set());
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(new Set());
  const [claimingAll, setClaimingAll] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  const { currentOrgId, currentOrgName } = useOrganizationStore();

  // Use notifications store for modal visibility and counts
  const isOpen = useNotificationsStore(state => state.showPendingLicensesModal);
  const setIsOpen = useNotificationsStore(state => state.setShowPendingLicensesModal);
  const setPendingLicensesCount = useNotificationsStore(state => state.setPendingLicensesCount);
  const setRejectedRequestsCount = useNotificationsStore(state => state.setRejectedRequestsCount);

  // Reset auto-open flag when org changes
  useEffect(() => {
    setHasAutoOpened(false);
  }, [currentOrgId]);

  // Subscribe to pending licenses + rejected requests; bell counts and modal
  // list update live whenever Firestore changes. Each subscription is gated
  // on the user's in-app preference for that notification type.
  useEffect(() => {
    if (!currentOrgId || !firebase) {
      setPendingLicenses([]);
      setRejectedRequests([]);
      setPendingLicensesCount(0);
      setRejectedRequestsCount(0);
      return;
    }

    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    setLoading(true);
    let cancelled = false;
    let unsubLicenses: (() => void) | undefined;
    let unsubRejected: (() => void) | undefined;

    (async () => {
      const prefs = await getNotificationPreferences(firebase, currentOrgId, userId);
      if (cancelled) return;

      // Track the first non-empty snapshot per org so we auto-open once.
      let autoOpenedThisOrg = false;
      const enabledLicenses = prefs.inApp.pendingLicenses;
      const enabledRejected = prefs.inApp.rejectedLicenseRequests;

      // If both are disabled, just clear and stop.
      if (!enabledLicenses && !enabledRejected) {
        setPendingLicenses([]);
        setRejectedRequests([]);
        setPendingLicensesCount(0);
        setRejectedRequestsCount(0);
        setLoading(false);
        return;
      }

      let licensesLoaded = !enabledLicenses;
      let rejectedLoaded = !enabledRejected;
      let currentLicenseCount = 0;
      let currentRejectedCount = 0;

      const finishLoadingIfReady = () => {
        if (licensesLoaded && rejectedLoaded) setLoading(false);
      };

      const tryAutoOpen = () => {
        if (!autoOpenedThisOrg && !hasAutoOpened && (currentLicenseCount > 0 || currentRejectedCount > 0)) {
          autoOpenedThisOrg = true;
          setIsOpen(true);
          setHasAutoOpened(true);
        }
      };

      if (enabledLicenses) {
        unsubLicenses = subscribePendingLicenses(firebase, currentOrgId, (licenses) => {
          setPendingLicenses(licenses);
          setPendingLicensesCount(licenses.length);
          currentLicenseCount = licenses.length;
          licensesLoaded = true;
          finishLoadingIfReady();
          tryAutoOpen();
        });
      } else {
        setPendingLicenses([]);
        setPendingLicensesCount(0);
      }

      if (enabledRejected) {
        unsubRejected = subscribeRejectedLicenseRequests(firebase, currentOrgId, (rejected) => {
          setRejectedRequests(rejected);
          setRejectedRequestsCount(rejected.length);
          currentRejectedCount = rejected.length;
          rejectedLoaded = true;
          finishLoadingIfReady();
          tryAutoOpen();
        });
      } else {
        setRejectedRequests([]);
        setRejectedRequestsCount(0);
      }

      finishLoadingIfReady();
    })();

    return () => {
      cancelled = true;
      if (unsubLicenses) unsubLicenses();
      if (unsubRejected) unsubRejected();
    };
  }, [currentOrgId, firebase, hasAutoOpened, setPendingLicensesCount, setRejectedRequestsCount, setIsOpen]);

  const handleClaimLicense = async (license: PendingLicense) => {
    if (!currentOrgId || !firebase?.auth?.currentUser) return;

    setClaimingIds((prev) => new Set(prev).add(license.id));

    try {
      // List + bell count update via the subscription once Firestore mutates.
      await claimPendingLicense(firebase, {
        sourceOrgId: license.sourceOrgId,
        trackId: license.trackId,
        licenseId: license.licenseId,
        claimingOrgId: currentOrgId,
        claimingUserId: firebase.auth.currentUser.uid,
        claimingOrgName: currentOrgName || undefined,
      });
    } catch (error) {
      console.error('Error claiming license:', error);
    } finally {
      setClaimingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(license.id);
        return newSet;
      });
    }
  };

  const handleClaimAll = async () => {
    if (!currentOrgId || !firebase?.auth?.currentUser) return;

    setClaimingAll(true);

    try {
      await Promise.all(
        pendingLicenses.map((license) =>
          claimPendingLicense(firebase, {
            sourceOrgId: license.sourceOrgId,
            trackId: license.trackId,
            licenseId: license.licenseId,
            claimingOrgId: currentOrgId,
            claimingUserId: firebase.auth.currentUser.uid,
            claimingOrgName: currentOrgName || undefined,
          })
        )
      );
    } catch (error) {
      console.error('Error claiming all licenses:', error);
    } finally {
      setClaimingAll(false);
    }
  };

  const handleDismissRejected = async (request: RejectedRequest) => {
    if (!firebase) return;

    setDismissingIds((prev) => new Set(prev).add(request.id));

    try {
      // List + bell count update via the subscription once Firestore mutates.
      await dismissRejectedLicenseRequest(firebase, {
        artistOrgId: request.artistOrgId,
        requestId: request.id,
      });
    } catch (error) {
      console.error('Error dismissing rejected request:', error);
    } finally {
      setDismissingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(request.id);
        return newSet;
      });
    }
  };

  const handleSkip = () => {
    setIsOpen(false);
  };

  // Close modal when all items are processed
  useEffect(() => {
    if (isOpen && pendingLicenses.length === 0 && rejectedRequests.length === 0 && !loading) {
      setIsOpen(false);
    }
  }, [pendingLicenses.length, rejectedRequests.length, isOpen, loading, setIsOpen]);

  const formatLicenseType = (type: string) => {
    switch (type) {
      case 'blanket':
        return 'Blanket License';
      case '1year':
        return '1 Year';
      case '2year':
        return '2 Years';
      case '3year':
        return '3 Years';
      case 'custom':
        return 'Custom Duration';
      default:
        return type;
    }
  };

  const totalItems = pendingLicenses.length + rejectedRequests.length;

  if (!isOpen || totalItems === 0) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleSkip}
      style={{
        color: '#ffffff',
        maxWidth: '600px',
        margin: '1.75rem auto',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleSkip}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={handleSkip}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>
          License Updates ({totalItems})
        </span>
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="light" />
          </div>
        ) : (
          <>
            {/* Pending Licenses Section */}
            {pendingLicenses.length > 0 && (
              <>
                <p style={{ marginBottom: '0.75rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                  <strong>Pending Licenses</strong> - Claim these to add to your library
                </p>
                <div className="pending-licenses-list" style={{ marginBottom: rejectedRequests.length > 0 ? '1.5rem' : 0 }}>
                  {pendingLicenses.map((license) => (
              <div
                key={license.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem 1rem',
                  marginBottom: '0.5rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 500,
                      marginBottom: '0.25rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {license.trackName}
                  </div>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: 'rgba(255, 255, 255, 0.5)',
                    }}
                  >
                    From: {license.sourceOrgName} | {formatLicenseType(license.licenseType)}
                    {license.dateExpires && (
                      <> | Expires: {moment(license.dateExpires).format('DD/MM/YYYY')}</>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  className="btn-primary-custom ml-3"
                  onClick={() => handleClaimLicense(license)}
                  disabled={claimingIds.has(license.id) || claimingAll}
                  style={{ minWidth: '80px' }}
                >
                  {claimingIds.has(license.id) ? (
                    <Spinner size="sm" />
                  ) : (
                    'Claim'
                  )}
                </Button>
              </div>
            ))}
                </div>
              </>
            )}

            {/* Rejected Requests Section */}
            {rejectedRequests.length > 0 && (
              <>
                <p style={{ marginBottom: '0.75rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                  <strong style={{ color: '#f5365c' }}>Rejected Requests</strong> - The following license requests were declined
                </p>
                <div className="rejected-requests-list">
                  {rejectedRequests.map((request) => (
                    <div
                      key={request.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        padding: '0.75rem 1rem',
                        marginBottom: '0.5rem',
                        backgroundColor: 'rgba(245, 54, 92, 0.1)',
                        borderRadius: '6px',
                        border: '1px solid rgba(245, 54, 92, 0.3)',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 500,
                            marginBottom: '0.25rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {request.trackName}
                        </div>
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: 'rgba(255, 255, 255, 0.5)',
                          }}
                        >
                          {formatLicenseType(request.licenseType)}
                          {request.rejectedAt && (
                            <> | Rejected: {moment(request.rejectedAt).format('DD/MM/YYYY')}</>
                          )}
                        </div>
                        {request.rejectionReason && (
                          <div
                            style={{
                              fontSize: '0.8rem',
                              color: 'rgba(255, 255, 255, 0.6)',
                              marginTop: '0.5rem',
                              fontStyle: 'italic',
                            }}
                          >
                            Reason: {request.rejectionReason}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className="btn-secondary-custom ml-3"
                        onClick={() => handleDismissRejected(request)}
                        disabled={dismissingIds.has(request.id)}
                        style={{ minWidth: '80px' }}
                      >
                        {dismissingIds.has(request.id) ? (
                          <Spinner size="sm" />
                        ) : (
                          'Dismiss'
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'space-between',
        }}
      >
        <Button
          className="btn-secondary-custom"
          onClick={handleSkip}
          disabled={claimingAll}
        >
          <span style={{ position: 'relative', zIndex: 2 }}>Remind Me Later</span>
        </Button>
        <Button
          className="btn-primary-custom"
          onClick={handleClaimAll}
          disabled={claimingAll || pendingLicenses.length === 0}
        >
          {claimingAll ? (
            <>
              <Spinner size="sm" className="mr-2" /> Claiming...
            </>
          ) : (
            <span style={{ position: 'relative', zIndex: 2 }}>
              Claim All ({pendingLicenses.length})
            </span>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PendingLicensesModal;
